import React, { useState, useEffect } from 'react';
import { Package, Truck, Clock, Download, CheckCircle, AlertTriangle, QrCode } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ItemsToShipPage = () => {
  const { user } = useApp();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      fetchItemsToShip();
    }
  }, [user]);

  const fetchItemsToShip = async () => {
    try {
      const response = await fetch(`${API}/api/seller/items-to-ship/${user.id}`);
      const data = await response.json();
      setItems(data.items_to_ship || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const markAsShipped = async (orderId) => {
    try {
      const response = await fetch(`${API}/api/seller/mark-shipped/${orderId}/${user.id}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        toast.success('Order marked as shipped!');
        fetchItemsToShip();
      } else {
        toast.error(data.error || 'Failed to mark as shipped');
      }
    } catch (error) {
      toast.error('Failed to mark as shipped');
    }
  };

  const downloadLabel = (labelUrl) => {
    if (labelUrl) {
      window.open(labelUrl, '_blank');
    } else {
      toast.info('Label will be available soon');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minted-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-7 w-7 text-minted-600" />
            Items to Ship
          </h1>
          <p className="text-gray-600 mt-1">
            {items.length} item{items.length !== 1 ? 's' : ''} waiting to be shipped
          </p>
        </div>

        {items.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">All caught up!</h3>
              <p className="text-gray-600 mt-2">You have no items waiting to be shipped.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <Card key={item.order_id} className="overflow-hidden">
                <CardContent className="p-0">
                  <div className="flex flex-col md:flex-row">
                    {/* Product Image */}
                    <div className="w-full md:w-32 h-32 bg-gray-100 flex-shrink-0">
                      {item.product_image ? (
                        <img
                          src={item.product_image}
                          alt={item.product_title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 p-4">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">{item.product_title}</h3>
                          <p className="text-sm text-gray-600 mt-1">Sold to: {item.buyer_name}</p>
                          
                          {/* Shipping Info */}
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              <Truck className="h-3 w-3" />
                              {item.shipping?.carrier || 'Carrier'} - {item.shipping?.service_level || 'Standard'}
                            </span>
                            {item.shipping?.tracking_number && (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-mono">
                                {item.shipping.tracking_number}
                              </span>
                            )}
                          </div>

                          {/* Deadline Warning */}
                          <div className="mt-2 flex items-center gap-1 text-sm">
                            <Clock className="h-4 w-4 text-orange-500" />
                            <span className="text-orange-600">Ship within {item.days_to_ship} days</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <Button
                            onClick={() => downloadLabel(item.shipping?.label_url)}
                            variant="outline"
                            size="sm"
                            className="flex items-center gap-2"
                          >
                            <Download className="h-4 w-4" />
                            Download Label
                          </Button>
                          <Button
                            onClick={() => markAsShipped(item.order_id)}
                            size="sm"
                            className="bg-minted-600 hover:bg-minted-700 flex items-center gap-2"
                          >
                            <CheckCircle className="h-4 w-4" />
                            Mark as Shipped
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Help Section */}
        <Card className="mt-8 bg-gradient-to-r from-minted-50 to-cyan-50 border-minted-200">
          <CardContent className="py-6">
            <h3 className="font-semibold text-minted-800 mb-2">How to ship your items:</h3>
            <ol className="list-decimal list-inside text-sm text-minted-700 space-y-2">
              <li>Download and print the shipping label</li>
              <li>Pack your item securely</li>
              <li>Attach the label to the outside of the package</li>
              <li>Drop off at the carrier location (Australia Post, etc.)</li>
              <li>Click "Mark as Shipped" to notify the buyer</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ItemsToShipPage;
